import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CreditCard, Plus, Wallet, TrendingUp, HandCoins } from "lucide-react";
import {
  usePayments,
  useReceivables,
  useRecordPayment,
  useMyLedger,
  ListPaymentsParams,
  OfflinePaymentMethod,
} from "../api/payments";
import { useClients } from "../api/clients";
import { useAuth } from "../context/AuthContext";
import { PaymentMethod } from "../types/models";
import { env } from "../config/env";
import { PageHeader } from "../components/ui/PageHeader";
import { StatCard } from "../components/ui/StatCard";
import { Pagination } from "../components/ui/Pagination";
import { StatusBadge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { Button, SelectField, TextField, TextareaField } from "../components/ui/Form";
import { SearchableSelect } from "../components/ui/SearchableSelect";
import { LoadingState, ErrorState, EmptyState } from "../components/ui/States";
import { toast } from "../components/ui/Toast";
import { getErrorMessage } from "../lib/errors";
import { formatCurrency, formatDate, humanizeEnum, toNumber } from "../utils/helpers";
import { StripePaymentModal } from "../components/StripePaymentModal";

const OFFLINE_METHODS: OfflinePaymentMethod[] = [
  PaymentMethod.CASH,
  PaymentMethod.BANK_TRANSFER,
  PaymentMethod.CHEQUE,
];

export const Payments: React.FC = () => {
  const { isManagement, isClient } = useAuth();
  const [page, setPage] = useState(1);
  const [method, setMethod] = useState<PaymentMethod | "">("");
  const [recording, setRecording] = useState(false);
  const [payingOnline, setPayingOnline] = useState(false);

  const receivables = useReceivables(isManagement);
  const myLedger = useMyLedger(isClient);

  const params: ListPaymentsParams = {
    page,
    limit: 10,
    method: method || undefined,
  };
  const { data, isLoading, isError, error, refetch, isFetching } = usePayments(params);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payments"
        subtitle={isClient ? "Your payment history and balance." : "Receivables and payment records."}
        actions={
          <>
            {isClient && env.stripePublishableKey && (
              <Button icon={<HandCoins size={18} />} onClick={() => setPayingOnline(true)}>
                Pay Online
              </Button>
            )}
            {isManagement && (
              <Button icon={<Plus size={18} />} onClick={() => setRecording(true)}>
                Record Payment
              </Button>
            )}
          </>
        }
      />

      {isManagement && receivables.data && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <StatCard icon={<Wallet size={22} className="text-red-400" />} label="Total Receivable" value={formatCurrency(receivables.data.totalReceivable)} hint={`${receivables.data.clientCount} clients`} accent="border-red-500" />
          <StatCard icon={<TrendingUp size={22} className="text-blue-400" />} label="Total Ordered" value={formatCurrency(receivables.data.totalOrdered)} accent="border-blue-500" />
          <StatCard icon={<CreditCard size={22} className="text-emerald-400" />} label="Total Collected" value={formatCurrency(receivables.data.totalPaid)} accent="border-emerald-500" />
        </div>
      )}

      {isClient && myLedger.data && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <StatCard icon={<TrendingUp size={22} className="text-blue-400" />} label="Total Ordered" value={formatCurrency(myLedger.data.summary.totalOrdered)} accent="border-blue-500" />
          <StatCard icon={<CreditCard size={22} className="text-emerald-400" />} label="Total Paid" value={formatCurrency(myLedger.data.summary.totalPaid)} accent="border-emerald-500" />
          <StatCard icon={<Wallet size={22} className="text-red-400" />} label="Outstanding" value={formatCurrency(myLedger.data.summary.remaining)} accent="border-red-500" />
        </div>
      )}

      <div className="card flex p-4">
        <select value={method} onChange={(e) => { setMethod(e.target.value as PaymentMethod | ""); setPage(1); }} className="input sm:w-56">
          <option value="">All methods</option>
          {Object.values(PaymentMethod).map((m) => (
            <option key={m} value={m}>{humanizeEnum(m)}</option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : data && data.items.length === 0 ? (
          <EmptyState icon={<CreditCard size={32} />} title="No payments yet" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3">Receipt</th>
                    {!isClient && <th className="px-4 py-3">Client</th>}
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3">Method</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody className={isFetching ? "opacity-60 transition-opacity" : ""}>
                  {data?.items.map((p) => (
                    <tr key={p.id} className="border-b border-slate-800 last:border-0 hover:bg-slate-800/40">
                      <td className="px-4 py-3 font-medium text-slate-100">{p.paymentNumber}</td>
                      {!isClient && <td className="px-4 py-3 text-slate-300">{p.client?.companyName ?? "—"}</td>}
                      <td className="px-4 py-3 text-right font-medium text-emerald-400">{formatCurrency(p.amount)}</td>
                      <td className="px-4 py-3 text-slate-300">{humanizeEnum(p.method)}</td>
                      <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-3 text-slate-400">{formatDate(p.paidAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={data?.meta} onPageChange={setPage} />
          </>
        )}
      </div>

      {recording && <RecordPaymentModal onClose={() => setRecording(false)} />}
      {payingOnline && (
        <StripePaymentModal
          outstanding={toNumber(myLedger.data?.summary.remaining)}
          onClose={() => setPayingOnline(false)}
        />
      )}
    </div>
  );
};

// ───────────────────── Record (offline) payment ─────────────────────

const paymentSchema = z.object({
  clientId: z.string().uuid("Select a client"),
  amount: z.coerce.number().positive("Amount must be greater than zero"),
  method: z.enum(["CASH", "BANK_TRANSFER", "CHEQUE"]),
  reference: z.string().optional(),
  note: z.string().optional(),
});

export const RecordPaymentModal: React.FC<{
  /** When set, the payment is locked to this client (no client picker shown). */
  lockedClient?: { id: string; companyName: string };
  onClose: () => void;
}> = ({ lockedClient, onClose }) => {
  const clients = useClients({ limit: 100 });
  const record = useRecordPayment();
  const { register, control, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(paymentSchema),
    defaultValues: { clientId: lockedClient?.id ?? "", method: PaymentMethod.CASH },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await record.mutateAsync({
        clientId: lockedClient?.id ?? values.clientId,
        amount: values.amount,
        method: values.method,
        reference: values.reference || undefined,
        note: values.note || undefined,
      });
      toast.success("Payment recorded");
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={lockedClient ? `Record Payment — ${lockedClient.companyName}` : "Record Payment"}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} loading={record.isPending}>Record</Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {lockedClient ? (
          <div className="rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2 text-sm">
            <span className="text-slate-400">Client: </span>
            <span className="font-medium text-slate-100">{lockedClient.companyName}</span>
          </div>
        ) : (
          <Controller
            control={control}
            name="clientId"
            render={({ field }) => (
              <SearchableSelect
                label="Client"
                required
                error={errors.clientId?.message}
                value={field.value ?? ""}
                onChange={field.onChange}
                options={(clients.data?.items ?? []).map((c) => ({ value: c.id, label: c.companyName }))}
                placeholder={clients.isLoading ? "Loading clients…" : "Select client…"}
              />
            )}
          />
        )}
        <div className="grid grid-cols-2 gap-4">
          <TextField label="Amount" type="number" step="0.01" required error={errors.amount?.message} {...register("amount")} />
          <SelectField label="Method" {...register("method")}>
            {OFFLINE_METHODS.map((m) => (
              <option key={m} value={m}>{humanizeEnum(m)}</option>
            ))}
          </SelectField>
        </div>
        <TextField label="Reference" placeholder="Cheque / transfer no. (optional)" {...register("reference")} />
        <TextareaField label="Note" {...register("note")} />
      </form>
    </Modal>
  );
};
